/**
 * @file orders.store.ts
 *
 * Zustand store for the Orders tab. Bespoke (unlike the products/categories
 * stores it does NOT compose {@link createFormSlice}, since orders are never
 * created or edited through a form): it pairs a server-paginated, filterable
 * list with a single-order slice for the details drawer (fetched fresh by id, as
 * the list rows carry only the lighter `/v1` summary) and the `/v1` order actions
 * (cancel, refund, delete, mark-paid, fulfil, status override). Mutations return
 * `true`/`false` and surface failures via `error`
 * (list) or `actionError`/`actionErrorCode` (the write actions, for the per-code
 * message remap); errors are coerced with {@link toMessage} / {@link toErrorCode}.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchOrders as apiFetchOrders,
  fetchOrderById as apiFetchOrderById,
  cancelOrder as apiCancelOrder,
  refundOrder as apiRefundOrder,
  deleteOrder as apiDeleteOrder,
  markOrderPaid as apiMarkOrderPaid,
  fulfilOrder as apiFulfilOrder,
  overrideOrderStatus as apiOverrideOrderStatus,
} from '../api/orders';
import type { FetchOrdersParams, ShopOrder } from '../api/shop-types';
import { toErrorCode, toMessage } from './store-utils';

/**
 * Shape of the orders store: the current page of orders plus a single "current"
 * order driving the details drawer and the in-place status update.
 */
interface OrdersState {
  /** The current page of orders. */
  orders: ShopOrder[];
  /** Total number of orders across all pages (from `X-Total-Count`). */
  ordersTotal: number;
  /** Total number of order pages (from `X-Total-Pages`). */
  ordersTotalPages: number;
  /** Whether a list fetch is in flight. */
  loading: boolean;
  /** Last list/mutation error message, or `null` on success. */
  error: string | null;

  /** The order shown in the details drawer, or `null` when closed. */
  currentOrder: ShopOrder | null;
  /** Whether {@link OrdersState.loadOrder} is fetching a single order. */
  orderLoading: boolean;
  /** Error from loading a single order (e.g. an unknown id). */
  orderError: string | null;

  /** Last cancel/refund error message, or `null` on success. */
  actionError: string | null;
  /** Last cancel/refund error code, for the per-code message remap, or `null`. */
  actionErrorCode: string | null;

  /**
   * Fetches a page of orders, replacing the current list.
   *
   * @param params - Optional pagination, status, and search filters.
   */
  fetchOrders: (params?: FetchOrdersParams) => Promise<void>;

  /**
   * Loads the full order by id to populate the details drawer. The list rows carry
   * only the lighter `/v1` summary (no line items, addresses, or itemised totals), so
   * this always fetches the full order rather than seeding from the list cache. The
   * result is stored on `currentOrder`.
   *
   * @param id - The order id to load.
   *
   * @returns The order, or `null` when the fetch fails.
   */
  loadOrder: (id: string) => Promise<ShopOrder | null>;

  /** Clears the current order (closes the details drawer). */
  clearOrder: () => void;

  /**
   * Cancels an order, updating it in the list and the drawer on success.
   *
   * @param id - The order id to cancel.
   * @param reason - Optional cancellation reason.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  cancelOrder: (id: string, reason?: string) => Promise<boolean>;

  /**
   * Fully refunds an order, updating it in the list and the drawer on success.
   *
   * @param id - The order id to refund.
   * @param opts - Optional refund reason and whether to restock the refunded items.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  refundOrder: (
    id: string,
    opts: { reason?: string; restock?: boolean },
  ) => Promise<boolean>;

  /**
   * Soft-deletes an order, removing it from the list and closing the drawer on success.
   * The engine gates deletion to `cancelled`/`completed` orders, so a live order yields
   * `false` with `actionErrorCode = 'order_not_deletable'`.
   *
   * @param id - The order id to delete.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  deleteOrder: (id: string) => Promise<boolean>;

  /**
   * Marks a manual order as paid, updating it in the list and the drawer on success.
   * Already-paid orders yield `false` with `actionErrorCode = 'order_already_paid'`.
   *
   * @param id - The order id to mark paid.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  markOrderPaid: (id: string) => Promise<boolean>;

  /**
   * Fulfils an order in full (one shipment over every line at its ordered quantity), then
   * re-fetches the order so the drawer reflects the new fulfilment status. The line items
   * are read from the order already in state (the drawer's full order).
   *
   * @param id - The order id to fulfil.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  fulfilOrder: (id: string) => Promise<boolean>;

  /**
   * Force-sets an order's status via the admin status-override backdoor (no money
   * moves), updating it in the list and the drawer on success.
   *
   * @param id - The order id.
   * @param status - The target status (shop vocabulary, e.g. `'on-hold'`).
   * @param reason - Optional audit reason; the API layer fills a canned default.
   *
   * @returns `true` on success, `false` when the request fails (see `actionErrorCode`).
   */
  overrideOrderStatus: (id: string, status: string, reason?: string) => Promise<boolean>;
}

/**
 * Monotonic token for {@link OrdersState.loadOrder}: a resolving fetch only writes
 * `currentOrder` when no newer load has started since, so a slow earlier response
 * cannot overwrite the order the drawer is now showing.
 */
let loadOrderSeq = 0;

export const useOrdersStore = create<OrdersState>()(
  devtools(
    (set, get) => ({
      orders: [],
      ordersTotal: 0,
      ordersTotalPages: 0,
      loading: false,
      error: null,

      currentOrder: null,
      orderLoading: false,
      orderError: null,

      actionError: null,
      actionErrorCode: null,

      fetchOrders: async (params = {}) => {
        set({ loading: true, error: null }, false, 'orders/fetch/pending');
        try {
          const res = await apiFetchOrders(params);
          set(
            {
              orders: res.data,
              ordersTotal: res.total,
              ordersTotalPages: res.totalPages,
              loading: false,
            },
            false,
            'orders/fetch/fulfilled',
          );
        } catch (err) {
          set({ loading: false, error: toMessage(err) }, false, 'orders/fetch/rejected');
        }
      },

      loadOrder: async (id) => {
        const seq = ++loadOrderSeq;
        set({ orderLoading: true, orderError: null }, false, 'orders/loadOrder/pending');
        try {
          const order = await apiFetchOrderById(id);
          if (seq !== loadOrderSeq) {
            // A newer load superseded this one; drop the stale write.
            return order;
          }
          set(
            { currentOrder: order, orderLoading: false },
            false,
            'orders/loadOrder/fulfilled',
          );
          return order;
        } catch (err) {
          if (seq !== loadOrderSeq) {
            return null;
          }
          set(
            { orderLoading: false, orderError: toMessage(err) },
            false,
            'orders/loadOrder/rejected',
          );
          return null;
        }
      },

      clearOrder: () => {
        set({ currentOrder: null, orderError: null }, false, 'orders/clearOrder');
      },

      cancelOrder: async (id, reason) => {
        set({ actionError: null, actionErrorCode: null }, false, 'orders/cancel/pending');
        try {
          const updated = await apiCancelOrder(id, reason);
          set(
            (state) => ({
              orders: state.orders.map((o) => (o.id === id ? updated : o)),
              currentOrder:
                state.currentOrder?.id === id ? updated : state.currentOrder,
            }),
            false,
            'orders/cancel/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/cancel/rejected',
          );
          return false;
        }
      },

      refundOrder: async (id, opts) => {
        set({ actionError: null, actionErrorCode: null }, false, 'orders/refund/pending');
        try {
          const updated = await apiRefundOrder(id, opts);
          set(
            (state) => ({
              orders: state.orders.map((o) => (o.id === id ? updated : o)),
              currentOrder:
                state.currentOrder?.id === id ? updated : state.currentOrder,
            }),
            false,
            'orders/refund/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/refund/rejected',
          );
          return false;
        }
      },

      deleteOrder: async (id) => {
        set({ actionError: null, actionErrorCode: null }, false, 'orders/delete/pending');
        try {
          await apiDeleteOrder(id);
          set(
            (state) => ({
              orders: state.orders.filter((o) => o.id !== id),
              currentOrder: state.currentOrder?.id === id ? null : state.currentOrder,
            }),
            false,
            'orders/delete/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/delete/rejected',
          );
          return false;
        }
      },

      markOrderPaid: async (id) => {
        set({ actionError: null, actionErrorCode: null }, false, 'orders/markPaid/pending');
        try {
          const updated = await apiMarkOrderPaid(id);
          set(
            (state) => ({
              orders: state.orders.map((o) => (o.id === id ? updated : o)),
              currentOrder:
                state.currentOrder?.id === id ? updated : state.currentOrder,
            }),
            false,
            'orders/markPaid/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/markPaid/rejected',
          );
          return false;
        }
      },

      fulfilOrder: async (id) => {
        set({ actionError: null, actionErrorCode: null }, false, 'orders/fulfil/pending');
        const order =
          get().currentOrder?.id === id
            ? get().currentOrder
            : get().orders.find((o) => o.id === id);
        const items = (order?.line_items ?? [])
          .filter((line) => line.shippable)
          .map((line) => ({ orderItemId: line.id, quantity: line.quantity }));
        try {
          await apiFulfilOrder(id, items);
          // The shipment response is not the order; re-fetch the full order so the drawer
          // reflects the re-projected fulfilment status.
          const updated = await apiFetchOrderById(id);
          set(
            (state) => ({
              orders: state.orders.map((o) => (o.id === id ? updated : o)),
              currentOrder:
                state.currentOrder?.id === id ? updated : state.currentOrder,
            }),
            false,
            'orders/fulfil/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/fulfil/rejected',
          );
          return false;
        }
      },

      overrideOrderStatus: async (id, status, reason) => {
        set(
          { actionError: null, actionErrorCode: null },
          false,
          'orders/overrideStatus/pending',
        );
        try {
          const updated = await apiOverrideOrderStatus(id, status, reason);
          set(
            (state) => ({
              orders: state.orders.map((o) => (o.id === id ? updated : o)),
              currentOrder:
                state.currentOrder?.id === id ? updated : state.currentOrder,
            }),
            false,
            'orders/overrideStatus/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { actionError: toMessage(err), actionErrorCode: toErrorCode(err) },
            false,
            'orders/overrideStatus/rejected',
          );
          return false;
        }
      },
    }),
    { name: 'shop/orders' },
  ),
);
